//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { testAPI } from "./testsAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type TestDetails,
  type CreateTestInput,
  type UpdateTestInput,
} from "./types/types";

export const useTests = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "tests",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      testAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const test = data?.test || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createTestMutation = useMutation({
    mutationFn: (testData: CreateTestInput) => testAPI.create(testData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tests"] });
    },
  });

  const updateTestMutation = useMutation({
    mutationFn: ({ id, testData }: UpdateTestInput) =>
      testAPI.update(id, testData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tests"] });
    },
  });

  const deleteTestMutation = useMutation({
    mutationFn: (id: string) => testAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tests"] });
    },
  });

  const getTestById = useCallback(
    async (id: string): Promise<TestDetails | null> => {
      try {
        const testDetails = await testAPI.getById(id, token);

        console.log("Fetched test details:", testDetails);
        return testDetails;
      } catch (err) {
        console.error("Error fetching test by ID:", err);
        return null;
      }
    },
    [token]
  );

  return {
    test, // Lista de testes
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createTests: createTestMutation.mutateAsync, // Agora retorna Promise<TestDetails>
    updateTests: updateTestMutation.mutateAsync, // Agora retorna Promise<void> ou conforme definido
    deleteTests: deleteTestMutation.mutateAsync, // Mantém mutateAsync
    getTestById,
    changePage,
    isCreating: createTestMutation.isPending,
    isUpdating: updateTestMutation.isPending,
    isDeleting: deleteTestMutation.isPending,
  };
};
